import { observer } from "mobx-react";
import { Box, VBox } from "@/components-v2/Box";
import { Alert } from "@/components-v2/Alert";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { FC, useEffect, useRef, useState } from "react";
import { Button } from "@/components-v2/Button";
import styles from "./Handwritten.module.css";
import { logError } from "@/utils/log";
import { TextInput } from "@/components-v2/TextInput";
import { SigningModel } from "./signingDomain";
import { ReactSketchCanvas } from "react-sketch-canvas";
import { getWindowDimensions } from "@/utils/other";
import WebFont from "webfontloader";
import { RadioButtonGroup } from "@/components-v2/RadioButton";
import { identity } from "lodash";
import { isMobileView } from "@/utils/screen";
import { Select } from "@/components-v2/Select";
import { Icon } from "@/components-v2/Icon";
import { TMessageKey } from "@/i18n/types.gen";
import { when } from "@/utils/functions";

import { useTranslation } from "@/stores/RootStoreContext";
import { SigningMethod } from "@/common/common";

enum Fonts {
  sacramento = "Sacramento",
  grechen = "Grechen Fuemen",
  dacing = "Dancing Script",
  parisienne = "Parisienne",
}

type InputMode = "input" | "draw";

export const Handwritten: FC<{
  signer: string;
  signingModel: SigningModel;
}> = ({ signer, signingModel }) => {
  const { t } = useTranslation();
  const sketchRef = useRef<ReactSketchCanvas>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [inputMode, setInputMode] = useState<InputMode>("input");
  const [signerInput, changeSigner] = useState<string>(signer);
  const [signFont, setFont] = useState<Fonts>(Fonts.sacramento);
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function handleResize() {
    setWindowDimensions(getWindowDimensions());
  }

  useEffect(() => {
    WebFont.load({
      google: {
        families: Object.values(Fonts),
      },
    });
  }, []);

  const generateFontStyle = () => ({
    "text-align": "center",
    width: "100%",
    border: "none",
    padding: 10,
    fontSize: 60,
    color: "darkBlue",
    fontFamily: `'${signFont}', cursive`,
  });

  const sendHandwrittenSignatureFromSketch = () => {
    if (!sketchRef.current || signingModel.isSendingHandwrittenSignature()) return;
    signingModel.setIsSendingHandwrittenSignature();
    sketchRef.current
      .exportImage("png")
      .then(signingModel.sendHandwrittenSignature)
      .catch((err) =>
        logError(err, "Failed to export handwritten signature as base64 encoded PNG"),
      );
  };

  const sendInputSignature = () => {
    if (!canvasRef?.current || signingModel.isSendingHandwrittenSignature()) return;
    signingModel.setIsSendingHandwrittenSignature();
    const context = canvasRef?.current?.getContext("2d");

    if (context) {
      context.clearRect(0, 0, 800, 150);
      context.font = `60px '${signFont}', cursive`;
      context.fillStyle = "darkBlue";
      context.fillText(signerInput, 30, 90);
      signingModel.sendHandwrittenSignature(canvasRef?.current.toDataURL());
    }
  };
  return (
    <VBox gap={"lg"} alignItems={isMobileView() ? "stretch" : "center"} padding>
      <Alert
        icon={{ size: "lg", name: "warning", weight: "regular" }}
        type={"info"}
        message={t("signing.sendCode.HANDWRITING", {
          signerName: signer,
        })}
      />
      <RadioButtonGroup<InputMode>
        name={"inputMode"}
        horizontal={!isMobileView()}
        options={["input", "draw"]}
        idFn={identity}
        labelFn={(value) =>
          value === "draw"
            ? t("signing.handWritten")
            : value === "input"
              ? t("signing.byTyping")
              : ""
        }
        onChange={(_, option) => setInputMode(option as InputMode)}
        selected={inputMode}
      />
      {inputMode === "input" && (
        <Select
          value={{
            code: signFont,
            name: signFont,
          }}
          items={Object.values(Fonts).map((item) => ({ code: item, name: item }))}
          onSelect={(item) => {
            setFont(item.code as Fonts);
          }}
          idFn={(item) => item.name}
          labelFn={(item) => item.name}
        />
      )}
      {inputMode === "draw" ? (
        <ReactSketchCanvas
          ref={sketchRef}
          className={styles.canvasStyleDraw}
          width="100%"
          height="400px"
          strokeWidth={windowDimensions.width / 350}
          strokeColor="darkBlue"
          exportWithBackgroundImage={false}
        />
      ) : (
        <div style={{ position: "relative" }}>
          <canvas className={styles.canvasStyleInput} width="800" height="150" ref={canvasRef} />
          <div className={styles.textWrap}>
            <input
              style={generateFontStyle()}
              value={signerInput}
              onChange={(e) => changeSigner(e.target.value)}
            />
          </div>
        </div>
      )}
      <Button
        variant={"primary"}
        label={
          signingModel.isSendingHandwrittenSignature()
            ? t("signing.signingInProgress")
            : t("signing.sign")
        }
        loading={signingModel.isSendingHandwrittenSignature()}
        onClick={() =>
          inputMode === "draw" ? sendHandwrittenSignatureFromSketch() : sendInputSignature()
        }
      />
    </VBox>
  );
};

const authCodeMessage = (signingMethod: SigningMethod | undefined): TMessageKey | undefined => {
  switch (signingMethod) {
    case SigningMethod.AUTH_EMAIL:
      return "signing.sendCode.AUTH_EMAIL";
    case SigningMethod.AUTH_SMS:
      return "signing.sendCode.AUTH_SMS";
    case SigningMethod.AUTH_CALL:
      return "signing.sendCode.AUTH_CALL";
    case SigningMethod.HANDWRITING:
      return "signing.sendCode.HANDWRITING";
  }
};

const AuthorizationCode: FC<{
  signingMethod: SigningMethod | undefined;
  signer: string;
  signingModel: SigningModel;
}> = ({ signingMethod, signer, signingModel }) => {
  const { t } = useTranslation();
  const [inputCode, setCode] = useState<string>("");
  return (
    <VBox gap={"lg"} alignItems={isMobileView() ? "stretch" : "center"} padding>
      {when(authCodeMessage(signingMethod), (message) => (
        <Alert
          icon={{ size: "lg", name: "warning", weight: "regular" }}
          type={"info"}
          message={t(message, { signerName: signer })}
        />
      ))}
      <TextInput
        label={t("signing.inputCode")}
        value={inputCode}
        onChange={(v) => setCode(v.target.value)}
      />
      <Button
        variant={"primary"}
        label={t("signing.verifySigningWithCode")}
        onClick={() => signingModel.codeVerify(inputCode)}
      />
    </VBox>
  );
};

export const SigningComponent = observer(
  ({
    signingModel,
    signingMethod,
    signerName,
  }: {
    signingModel: SigningModel;
    signingMethod: SigningMethod | undefined;
    signerName: string;
  }) => {
    const { t } = useTranslation();
    switch (signingModel.state) {
      case "documentSigned":
        return (
          <Box alignItems={"center"} justifyContent={"center"} vertical padding>
            <Icon name={"check-circle"} size={"jumbo"} color={"success"} />
            <h4>{t("signing.documentSigned")}</h4>
            <div>{t("signing.documentSigned.description")}</div>
          </Box>
        );
      case "awaitHandwrittenSignature":
        return <Handwritten signer={signerName} signingModel={signingModel} />;
      case "authorizationCodeSent":
        return (
          <AuthorizationCode
            signingMethod={signingMethod}
            signer={signerName}
            signingModel={signingModel}
          />
        );
      case "errorVerifyingAuthorizationCode":
      case "errorHandlingSignRequest":
        return (
          <Box alignItems={"center"} justifyContent={"center"} vertical padding>
            <Icon name={"warning-circle"} size={"jumbo"} color={"warning"} />
            <div>{t("signing.fetchedError")}</div>

            {signingModel.state === "errorVerifyingAuthorizationCode" && (
              <>
                <Button
                  variant={"primary"}
                  label={t("signing.tryAgain")}
                  onClick={() => {
                    signingModel.signRequest(signingMethod);
                  }}
                />
              </>
            )}
          </Box>
        );
      case "errorSendingHandwrittenSignature":
        return (
          <VBox padding>
            <Box>
              <Alert message={t("error.signing")} type="error" />;
            </Box>
            <Box>
              <Button
                variant={"primary"}
                onClick={() => window.location.reload()}
                label={t("try-again")}
              />
            </Box>
          </VBox>
        );
      default:
        return <LoadingIndicator fullscreen />;
    }
  },
);
